import React, {FC, useEffect, useRef, useState} from 'react';
import {useMap} from 'react-map-gl/maplibre';
import {
  TerraDraw,
  TerraDrawMouseEvent,
  TerraDrawPolygonMode,
  TerraDrawSelectMode
} from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';
import {nearestPointOnLine, point, lineString} from '@turf/turf';
import {LineString, MultiPolygon, Polygon} from 'geojson';
import {Project, Unproject} from 'terra-draw';

const customSnap = (
  event: TerraDrawMouseEvent,
  context: {
        currentId?: string | number;
        currentCoordinate?: number;
        getCurrentGeometrySnapshot: () => (Polygon | LineString) | null;
        project: Project;
        unproject: Unproject;
    },
  snapGeometry: Polygon,
  pixelPrecision: number
) => {
  const userPoint = point([event.lng, event.lat]);
  const nearestPoint = nearestPointOnLine(lineString(snapGeometry.coordinates[0]), userPoint);
  const {x, y} = context.project(nearestPoint.geometry.coordinates[0], nearestPoint.geometry.coordinates[1]);

  if (Math.abs(x - event.containerX) < pixelPrecision && Math.abs(y - event.containerY) < pixelPrecision){
    return nearestPoint.geometry.coordinates;
  }
  return userPoint.geometry.coordinates;
};


const simplifyGeometry = (geometry: Polygon | MultiPolygon): Polygon => {
  if (geometry.type === 'MultiPolygon'){
    return {
      ...geometry,
      'type': 'Polygon',
      'coordinates': [geometry.coordinates[0][0]] // HACK, terradraw no soporta MultiPolygon, Tomamos el anillo exterior
    };
  }
  return geometry;
};

export type GeometryEditorProps = {
  geometry: Polygon | MultiPolygon,
  onGeomChanged: (geometry: Polygon) => void,
  pixelPrecision?: number
};

const GeometryEditor: FC<GeometryEditorProps> = ({
  geometry, 
  onGeomChanged, 
  pixelPrecision = 20 // default to 20px precision
}) => {
  const draw = useRef<TerraDraw | null>(null);
  const {current: map} = useMap();
  const [mode] = useState<string>('select');
  const [snapGeometry] = useState<Polygon>(simplifyGeometry(geometry)); //usamos la misma geometria para snaping
  const [featureId, setFeatureId] = useState<string | null>(null);

  useEffect(() => {
    if (map) {
      draw.current = new TerraDraw({
        tracked: true,
        adapter: new TerraDrawMapLibreGLAdapter({ map: map.getMap() }),
        modes: [
          new TerraDrawPolygonMode({
            editable: true,
            showCoordinatePoints: true,
            styles: {
              fillColor: '#00FFFF',
            }
          }),
          new TerraDrawSelectMode({
            allowManualDeselection: false,
            flags: {
              polygon: {
                feature: {
                  coordinates: {
                    snappable: {
                      toLine: true,
                      toCoordinate: true,
                      toCustom: (evt, context) => 
                        customSnap(evt, context, snapGeometry, pixelPrecision),
                    },
                    midpoints: true,
                    draggable: true,
                    deletable: true,
                  },
                },
              },
            }
          })
        ],
      });

      console.log('Starting TerraDraw');
      draw.current.start();
      draw.current.setMode('select');
    }

    return () => {
      console.log('Stopping TerraDraw');
      draw.current?.stop();
    };
  }, [map]);

  useEffect(() => {
    if (draw.current){
      const id = draw.current.getFeatureId() as string;
      setFeatureId(id);
      const result = draw.current.addFeatures([
        {
          'type': 'Feature',
          'properties': {
            'mode': 'polygon',
          },
          'geometry': simplifyGeometry(geometry),
          id: id
        }
      ]);
      const featureAddedValidation = result[0];
      if (featureAddedValidation.valid){
        draw.current.selectFeature(id);
        draw.current.setMode(mode);
      }
    }
  }, [draw, geometry]);

  const handleGeomChanged = (featureId: string) => {
    const feature = draw.current?.getSnapshotFeature(featureId);
    if (feature) onGeomChanged(feature.geometry as Polygon);
  };

  useEffect(() => {
    if (draw.current && featureId){
      draw.current.on('finish', () => handleGeomChanged(featureId));
    }
  }, [featureId]);

  return <></>;
};

export default GeometryEditor;